// @/hooks/useContentSidebar.js
import { useStaticQuery, graphql } from 'gatsby';
import { useLanguage } from '@/components/providers/LanguageContext';
import { useMemo } from 'react';

/**
 * Hook to fetch Sidebar Navigation content from Gatsby's GraphQL layer
 * Uses language context to automatically update content when language changes
 *
 * Uses useMemo instead of useState/useEffect to ensure synchronous content
 * resolution - critical for SSR/hydration consistency and Head component
 *
 * @param {string} [fallbackLocale='en'] - Fallback locale if context not available or locale not found
 * @returns {Object|null} Content for requested locale or null if no content exists at all
 */
export const useContentSidebar = (fallbackLocale = 'en') => {
  // Get current locale from context or use fallback
  const { locale } = useLanguage?.() || { locale: fallbackLocale };

  // Execute the GraphQL query
  const data = useStaticQuery(graphql`
    query {
      allContentSidebar {
        nodes {
          id
          overviewLabel
          selfAssessmentLabel
          carbonAccountingLabel
          supplyChainManagementLabel
          helpLabel
          settingsLabel
          companyProfileLabel
          userManagementLabel
          signOutLabel
          privacyPolicyLabel
          termsLabel
          changelogLabel
          aboutLabel
          companySettingsLabel
          accountLabel
          locale
        }
      }
    }
  `);

  // Use useMemo for synchronous content resolution (critical for SSR/hydration)
  const content = useMemo(() => {
    const nodes = data.allContentSidebar?.nodes || [];

    // First try the requested locale
    const localeContent = nodes.find(node => node.locale === locale);

    // Fall back to English if the requested locale isn't available
    const englishContent = locale !== 'en' ? nodes.find(node => node.locale === 'en') : null;

    return localeContent || englishContent || null;
  }, [locale, data.allContentSidebar?.nodes]);

  return content;
};

/**
 * Fallback version for components that don't have access to the language context
 * @param {string} locale - Desired locale (defaults to 'en')
 * @returns {Object|null} Content for requested locale or null
 */
export const getContentSidebarForLocale = (locale = 'en') => {
  // This function can be used in non-React contexts or for SSR
  try {
    if (typeof window !== 'undefined' && window.__GATSBY_INITIAL_DATA__) {
      // Attempt to access GraphQL data from Gatsby
      const staticQueries = window.__GATSBY_INITIAL_DATA__.staticQueryResults || {};

      for (const queryId in staticQueries) {
        const queryResult = staticQueries[queryId];
        const nodes = queryResult?.data?.allContentSidebar?.nodes || [];

        if (nodes.length > 0) {
          // First try the requested locale
          const localeContent = nodes.find(node => node.locale === locale);

          // Fall back to English if the requested locale isn't available
          const englishContent = locale !== 'en' ? nodes.find(node => node.locale === 'en') : null;

          return localeContent || englishContent || null;
        }
      }
    }
  } catch (error) {
    console.error('Error accessing content:', error);
  }

  return null;
};